import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useWallet } from '../contexts/WalletContext'
import { 
  Search, 
  Filter, 
  Star, 
  Download, 
  Eye, 
  DollarSign, 
  Users, 
  TrendingUp,
  Sparkles,
  Plus,
  Crown,
  Zap
} from 'lucide-react'

interface Template {
  id: string
  name: string
  category: string
  description: string
  preview: string
  price: number
  creator: string
  creatorName: string
  rating: number
  downloads: number
  tags: string[]
  features: string[]
  isPremium: boolean
  revenue: number
  createdAt: string
}

interface TemplateCategory {
  id: string
  name: string
  icon: React.ReactNode
  count: number
}

const categories: TemplateCategory[] = [
  { id: 'all', name: 'All Templates', icon: <Sparkles className="w-4 h-4" />, count: 0 },
  { id: 'music', name: 'Music', icon: <Zap className="w-4 h-4" />, count: 0 },
  { id: 'artist', name: 'Artist', icon: <Star className="w-4 h-4" />, count: 0 },
  { id: 'portfolio', name: 'Portfolio', icon: <Users className="w-4 h-4" />, count: 0 },
  { id: 'business', name: 'Business', icon: <Crown className="w-4 h-4" />, count: 0 },
  { id: 'blog', name: 'Blog', icon: <TrendingUp className="w-4 h-4" />, count: 0 }
]

export default function TemplateMarketplace() {
  const { isConnected, address } = useWallet()
  const [templates, setTemplates] = useState<Template[]>([])
  const [filteredTemplates, setFilteredTemplates] = useState<Template[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [sortBy, setSortBy] = useState<string>('popular')
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000])
  const [showPremiumOnly, setShowPremiumOnly] = useState<boolean>(false)

  // Mock data - in production this would come from blockchain
  useEffect(() => {
    const mockTemplates: Template[] = [
      {
        id: '1',
        name: 'Music Creator Pro',
        category: 'music',
        description: 'Professional template for musicians with music player, tour dates, and merchandise showcase.',
        preview: '/api/placeholder/400/300',
        price: 50,
        creator: '0x123...abc',
        creatorName: 'DJ Harmony',
        rating: 4.9,
        downloads: 1250,
        tags: ['music', 'player', 'tour', 'merchandise'],
        features: ['Music Player', 'Tour Dates', 'Merchandise', 'Social Links'],
        isPremium: true,
        revenue: 62500,
        createdAt: '2024-01-15'
      },
      {
        id: '2',
        name: 'Visual Artist Studio',
        category: 'artist',
        description: 'Stunning portfolio template for visual artists and photographers.',
        preview: '/api/placeholder/400/300',
        price: 35,
        creator: '0x456...def',
        creatorName: 'Art Creator',
        rating: 4.8,
        downloads: 890,
        tags: ['portfolio', 'gallery', 'artist', 'photography'],
        features: ['Portfolio Gallery', 'Commission Info', 'Contact Form'],
        isPremium: false,
        revenue: 31150,
        createdAt: '2024-01-20'
      },
      {
        id: '3',
        name: 'Creator Hub',
        category: 'portfolio',
        description: 'All-in-one platform for content creators with monetization features.',
        preview: '/api/placeholder/400/300',
        price: 75,
        creator: '0x789...ghi',
        creatorName: 'Content Master',
        rating: 4.9,
        downloads: 2100,
        tags: ['creator', 'monetization', 'subscription', 'analytics'],
        features: ['Content Gallery', 'Subscription Tiers', 'Analytics', 'Blog'],
        isPremium: true,
        revenue: 157500,
        createdAt: '2024-01-10'
      }
    ]
    
    setTemplates(mockTemplates)
    setFilteredTemplates(mockTemplates)
  }, [])

  // Filter and search templates
  useEffect(() => {
    let filtered = templates

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(template => template.category === selectedCategory)
    }

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(template => 
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    }

    // Price filter
    filtered = filtered.filter(template => 
      template.price >= priceRange[0] && template.price <= priceRange[1]
    )

    // Premium filter
    if (showPremiumOnly) {
      filtered = filtered.filter(template => template.isPremium)
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'popular':
          return b.downloads - a.downloads
        case 'rating':
          return b.rating - a.rating
        case 'price-low':
          return a.price - b.price
        case 'price-high':
          return b.price - a.price
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        default:
          return 0
      }
    })

    setFilteredTemplates(filtered)
  }, [templates, selectedCategory, searchQuery, sortBy, priceRange, showPremiumOnly])

  const handlePurchaseTemplate = (template: Template) => {
    if (!isConnected) {
      alert('Please connect your wallet to purchase templates')
      return
    }
    
    // In production, this would interact with smart contracts
    console.log('Purchasing template:', template.name)
    alert(`🎉 Template "${template.name}" purchased successfully! You can now use it in your website builder.`)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-purple-900 text-white">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
              radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
            `,
            backgroundSize: '100% 100%, 800px 800px, 900px 900px'
          }}
        />
        
        <div className="relative z-10 py-20 px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h1 
              className="text-5xl md:text-7xl font-bold mb-6"
              style={{ 
                fontFamily: 'Georgia, serif',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                background: 'linear-gradient(45deg, #F59E0B, #8B5CF6, #EF4444)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Template Marketplace
            </h1>
            <p className="text-xl md:text-2xl text-purple-200 mb-8 max-w-3xl mx-auto">
              Discover, purchase, and earn from beautiful website templates. 
              Mint your designs as NFTs and earn revenue every time they're used!
            </p>
            
            <div className="flex gap-4 justify-center mb-8">
              <Link
                to="/create-template"
                className="bg-gold-500 text-black px-8 py-4 rounded-full text-xl font-semibold hover:bg-gold-600 transition-colors flex items-center gap-3"
              >
                <Plus className="w-6 h-6" />
                Create Template
              </Link>
              <Link
                to="/creator-dashboard"
                className="bg-white/20 text-white border border-white/30 px-8 py-4 rounded-full text-xl font-semibold hover:bg-white/30 transition-colors flex items-center gap-3"
              >
                <TrendingUp className="w-6 h-6" />
                Creator Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="py-8 px-6 border-b border-white/20">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-200 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-200 focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                />
              </div>
            </div>

            {/* Sort */}
            <div className="flex gap-4">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-gold-400 focus:border-transparent"
              >
                <option value="popular">Most Popular</option>
                <option value="rating">Highest Rated</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="newest">Newest First</option>
              </select>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="premium-only"
                  checked={showPremiumOnly}
                  onChange={(e) => setShowPremiumOnly(e.target.checked)}
                  className="w-4 h-4 text-gold-600 focus:ring-gold-500 border-white/30 rounded bg-white/10"
                />
                <label htmlFor="premium-only" className="text-purple-200 flex items-center gap-2">
                  <Crown className="w-4 h-4 text-gold-400" />
                  Premium Only
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="py-6 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  selectedCategory === category.id
                    ? 'bg-gold-500 text-black'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {category.icon}
                {category.name}
                <span className="text-xs bg-white/20 px-2 py-1 rounded-full">
                  {filteredTemplates.filter(t => 
                    category.id === 'all' || t.category === category.id
                  ).length}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTemplates.map((template) => (
              <div
                key={template.id}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl overflow-hidden hover:bg-white/15 transition-all duration-300 group"
              >
                {/* Template Preview */}
                <div className="aspect-video bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center relative">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Eye className="w-8 h-8 text-white" />
                    </div>
                    <p className="text-white text-sm">{template.name}</p>
                  </div>
                  
                  {/* Premium Badge */}
                  {template.isPremium && (
                    <div className="absolute top-3 left-3">
                      <span className="bg-gold-500 text-black px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                        <Crown className="w-4 h-4" />
                        Premium
                      </span>
                    </div>
                  )}
                  
                  {/* Price */}
                  <div className="absolute top-3 right-3">
                    <span className="bg-black/50 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {template.price} SUI
                    </span>
                  </div>
                </div>

                {/* Template Info */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-semibold text-white line-clamp-2">{template.name}</h3>
                    <div className="flex items-center gap-1 text-yellow-400">
                      <Star className="w-4 h-4 fill-current" />
                      <span className="text-sm">{template.rating}</span>
                    </div>
                  </div>
                  
                  <p className="text-purple-200 text-sm mb-4 line-clamp-2">{template.description}</p>
                  
                  {/* Creator */}
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-6 h-6 bg-gold-500/20 rounded-full flex items-center justify-center">
                      <span className="text-gold-400 text-xs font-semibold">
                        {template.creatorName.charAt(0)}
                      </span>
                    </div>
                    <span className="text-purple-200 text-sm">by {template.creatorName}</span>
                  </div>

                  {/* Features */}
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-1">
                      {template.features.slice(0, 3).map((feature, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded"
                        >
                          {feature}
                        </span>
                      ))}
                      {template.features.length > 3 && (
                        <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs rounded">
                          +{template.features.length - 3}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center justify-between text-sm text-purple-200 mb-4">
                    <div className="flex items-center gap-1">
                      <Download className="w-4 h-4" />
                      {template.downloads.toLocaleString()}
                    </div>
                    <div className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      {(template.revenue / 100).toLocaleString()} earned
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePurchaseTemplate(template)}
                      className="flex-1 bg-gold-500 text-black py-2 px-4 rounded-lg hover:bg-gold-600 transition-colors font-semibold"
                    >
                      Purchase
                    </button>
                    <button className="bg-white/10 text-white py-2 px-4 rounded-lg hover:bg-white/20 transition-colors">
                      Preview
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredTemplates.length === 0 && (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold text-white mb-4">No templates found</h3>
              <p className="text-purple-200 mb-8">
                Try adjusting your search criteria or browse all templates
              </p>
              <button
                onClick={() => {
                  setSearchQuery('')
                  setSelectedCategory('all')
                  setShowPremiumOnly(false)
                }}
                className="bg-gold-500 text-black px-6 py-3 rounded-lg hover:bg-gold-600 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
